import { colors } from '@mui/material'
import { createTheme, responsiveFontSizes } from '@mui/material/styles'
import { ptBR } from '@mui/material/locale'

const theme = createTheme(
  {
    palette: {
      primary: {
        light: '#8D3D35',
        main: '#702826',
        dark: '#4F0C17',
      },
      /*
       * O dourado da marca. Mede 2,87:1 contra o branco — abaixo dos 4,5:1 da WCAG 1.4.3,
       * e abaixo até dos 3:1 de texto grande. Atinge todo botão `secondary` e o título dos
       * accordions (`Typography color='secondary'`).
       *
       * O alcance é maior do que 1.4.3: este mesmo token pinta `MuiRadio` (linha 96) e
       * `MuiCheckbox` (linha 104), então também falha **WCAG 1.4.11** (contraste
       * não-textual, limite do controle) — cujo mínimo é 3:1, perdido por 0,13. Medido no
       * gate G-a11y da HU 842638; a nota anterior tratava o débito como se fosse só texto,
       * o que subdimensionava a decisão de design system.
       *
       * Escurecer para 4,93:1 foi tentado em 2026-07-20 e REVERTIDO em 2026-07-21: mudava
       * visivelmente a identidade em todo o app. A cor é a de produção há muito tempo, o
       * problema não foi introduzido por esta leva, e trocar o dourado da marca é decisão
       * de design system — não de um MR de integração.
       *
       * Débito em mac-docs/engenharia/research/2026-08-05T1400_debito-contraste-dourado-da-
       * marca.md (o registro estava só prometido aqui, não existia). NÃO altere o token sem
       * essa decisão: o teste em index.test.ts trava o valor de propósito.
       */
      secondary: {
        light: '#F6F1EB',
        main: '#B79266',
        dark: '#8C6A42',
        contrastText: '#FFFFFF',
      },
      /**
       * WCAG 1.4.3 — o `warning` default do MUI (#ed6c02) com texto branco mede 3,11:1;
       * atinge o Chip "Repactuada". Escurecido até 5,06:1.
       */
      warning: {
        light: '#E9812B',
        main: '#B35300',
        dark: '#8A3F00',
        contrastText: '#FFFFFF',
      },
      info: {
        light: '#77A0b7',
        main: '#5689A6',
        dark: '#3C5F74',
        contrastText: '#FFFFFF',
      },
      grey: {
        200: '#EAEAEA',
      },
      text: {
        primary: colors.grey[800],
        // grey[50] é cor de fundo; como texto fica ilegível (ex.: DialogContentText padrão).
        secondary: colors.grey[700],
        disabled: colors.grey[800],
      },
      background: {
        paper: colors.grey[100],
        default: '#FFFFFF',
      },
    },
  },
  ptBR,
)

const defaultTheme = createTheme(theme, {
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          width: 260,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          ':hover': {
            backgroundColor: theme.palette.secondary.light,
            color: theme.palette.secondary.dark,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: theme.palette.secondary.main,
          fontWeight: 'bold',
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: theme.palette.secondary.main,
          fontSize: 14,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: theme.palette.secondary.main,
          fontSize: 14,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: 14,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: theme.palette.text.primary,
          fontWeight: 500,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    /**
     * WCAG 2.4.7 — o reset do MUI zera o outline nativo e o único sinal de foco de teclado
     * era a ondulação do ripple, que some. Medido: repouso e foco eram IDÊNTICOS
     * (outline none, background transparente, sem sombra) mesmo com `.Mui-focusVisible`
     * aplicado. Aqui o contorno volta — em MuiButtonBase, portanto vale para Button,
     * IconButton, Radio, Checkbox, Tab, MenuItem e afins de uma vez.
     */
    /*
     * Alvos de toque de 44x44 (WCAG 2.5.8, nível AAA) e borda de input mais escura
     * (1.4.11) foram REVERTIDOS em 2026-07-21: deixavam a interface visivelmente mais
     * pesada que produção — radios e ícones maiores, bordas mais fortes. O tamanho
     * nativo do MUI (IconButton 40x40, Radio padding 9) atende os 24x24 exigidos pela
     * 2.5.8 de nível AA da WCAG 2.2.
     *
     * O indicador de foco abaixo FICA: é um dos 4 críticos da auditoria e só desenha
     * no foco por teclado, sem afetar o layout em repouso.
     */
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': {
            outline: `3px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderColor: theme.palette.grey[400],
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: theme.palette.text.disabled,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .Mui-disabled': {
            backgroundColor: theme.palette.grey[200],
          },
          '& .MuiFormLabel-root': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .Mui-disabled': {
            backgroundColor: theme.palette.grey[200],
          },
        },
      },
    },
  },
})

export default responsiveFontSizes(defaultTheme)
